/**
 * formatDate — dates for the screen that never read "Invalid Date".
 *
 * `new Date(undefined).toLocaleDateString()` renders "Invalid Date", and the
 * product audit found it on SOW cards on both the client and the consultant
 * side (findings S12 and C5, 2026-10-28). These helpers return an empty
 * string for anything that is not a real date, so a caller can render
 * nothing, or its own "Not set", instead of the browser's words.
 */

export type DateInput = string | number | Date | null | undefined;

const DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

/** The Date for a value, or null when the value is missing or not a date. */
export function toValidDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A formatted date, or '' when there is no valid date to format. */
export function formatDate(value: DateInput, options: Intl.DateTimeFormatOptions = DEFAULT_OPTIONS, locale?: string): string {
  const d = toValidDate(value);
  return d ? d.toLocaleDateString(locale, options) : '';
}

/**
 * "Jan 5, 2026 - Mar 2, 2026" when both ends are dates, "from Jan 5, 2026"
 * or "until Mar 2, 2026" when only one is, '' when neither is.
 */
export function formatDateRange(start: DateInput, end: DateInput, options: Intl.DateTimeFormatOptions = DEFAULT_OPTIONS, locale?: string): string {
  const a = formatDate(start, options, locale);
  const b = formatDate(end, options, locale);
  if (a && b) return `${a} - ${b}`;
  if (a) return `from ${a}`;
  if (b) return `until ${b}`;
  return '';
}
